import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}

// NOTE: the no-free-plan subscription gate is enforced app-wide by
// <SubscriptionGate> (src/components/auth/SubscriptionGate.tsx), which also
// covers public routes. ProtectedRoute only handles authentication + admin role.
export default function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false }: ProtectedRouteProps) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }
      setAuthenticated(true);

      if (requireAdmin || requireSuperAdmin) {
        // NOTE: _user_id is passed from session.user.id (JWT-derived, not spoofable in normal flow).
        // An authenticated user could modify the RPC call via DevTools to probe other users' roles.
        // B13 REVOKE blocks anonymous probing. Authenticated probing is a minor info-leak (requires
        // knowing target UUIDs). A future fix would add auth.uid() = _user_id checks inside the SQL
        // functions, but this requires careful coordination with RLS policies that call them.
        const { data: roleData } = await supabase.rpc("is_super_admin", { _user_id: session.user.id });
        const { data: orgRole } = await supabase.rpc("get_user_org_role", { _user_id: session.user.id });

        const superAdmin = roleData === true;
        setIsSuperAdmin(superAdmin);
        setIsAdmin(orgRole === "admin" || superAdmin);
      }
      setLoading(false);
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthenticated(!!session);
      if (!session) setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [requireAdmin, requireSuperAdmin]);

  if (loading) return null;
  if (!authenticated) return <Navigate to="/auth" replace />;
  if (requireSuperAdmin && !isSuperAdmin) return <Navigate to="/" replace />;
  if (requireAdmin && !isAdmin) return <Navigate to="/" replace />;

  return <>{children}</>;
}
